/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: VoiceGuideFileBrowserModal.tsx
 🗓️ Created: 2026-03-24
 🧩 Description:
    Modal wrapper for VoiceGuideFileBrowser.
    Opens from the form header icon and allows browsing + playing
    all generated audio files without leaving the edit form.
────────────────────────────────────────────────────────────────*/

import { useEffect, useCallback } from 'react';
import { X, FolderOpen } from 'lucide-react';
import { VoiceGuideFileBrowser } from './VoiceGuideFileBrowser';
import { useI18n } from '../../../hooks/useI18n';

interface VoiceGuideFileBrowserModalProps {
  isOpen: boolean;
  onClose: () => void;
  guideId: number;
  soundPath: string;
  guideName?: string;
}

export function VoiceGuideFileBrowserModal({
  isOpen,
  onClose,
  guideId,
  soundPath,
  guideName,
}: VoiceGuideFileBrowserModalProps) {
  const { t } = useI18n();

  // Close on Escape
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('voiceGuides:browser.modalTitle') || 'Audio Files Browser'}
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative z-10 w-full max-w-3xl flex flex-col rounded-xl bg-card border border-border shadow-2xl max-h-[85vh]">

        {/* Modal header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/40 rounded-t-xl flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-primary/10 rounded-md">
              <FolderOpen className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                {t('voiceGuides:browser.modalTitle') || 'Audio Files'}
              </h3>
              {guideName && (
                <p className="text-xs text-muted-foreground">{guideName}</p>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Modal body — scrollable */}
        <div className="flex-1 overflow-y-auto p-4 min-h-0">
          <VoiceGuideFileBrowser guideId={guideId} soundPath={soundPath} />
        </div>
      </div>
    </div>
  );
}
